import { type Employee } from "@finway-group/shared/lib/models"
import { useCallback } from "react"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useSubCostCenters } from "~Shared/hooks/costCenter.hooks"
import { useEmployees, useLoggedInEmployeeProfile } from "~Shared/queries/employee.queries"
import { PurchaseOrderService } from "../service/purchaseOrder.service"
import { type PurchaseOrderFormValues } from "../types/PurchaseOrder.interfaces"

export const usePurchaseOrdersEmployees = () => {
    // Add these hooks for employee data
    const { fallbackApprover } = useCompany()
    const { data: employees } = useEmployees({ excludeDeleted: true, includeAutoApprover: true })
    const loggedInUser = useLoggedInEmployeeProfile()
    const costCenters = useSubCostCenters(true)

    const getProposedApprovers = useCallback(
        (purchaseOrder: PurchaseOrderFormValues) => {
            // CHECK IF OBJECT IS EMPTY ON FIRST
            if (Object.keys(purchaseOrder).length === 0 || !employees || !costCenters || !loggedInUser) {
                return []
            }

            const updatedProposedApprovers = PurchaseOrderService.determinePossibleApproversForPurchaseOrder({
                purchaseOrder,
                employees,
                costCenters,
                loggedInUser,
                fallbackApproverId: fallbackApprover,
            })

            return updatedProposedApprovers
        },
        [employees, costCenters, loggedInUser],
    )

    const selectApproverByCostCenter = (costCenter: string, approvers: Array<Employee>) => {
        if (!employees) {
            return fallbackApprover
        }

        const responsiblePerson = costCenters.find((cc) => cc._id === costCenter)?.responsibleUser
        const approver = approvers.find((approver) => approver.id === responsiblePerson)

        // if approver is found, return the approver
        if (approver) {
            return approver.id
        }

        // if no approver, return the superior
        if (!approver && responsiblePerson) {
            const userSuperior = employees?.find((employee) => employee.id === responsiblePerson)?.activeCompanyProfile?.superior
            if (userSuperior) {
                return userSuperior
            }
        }

        return fallbackApprover
    }

    const eligibleRequesters = employees
        ?.filter((employee) => !employee.activeCompanyProfile.deleted)
        .filter((employee) => {
            if (loggedInUser?.email === PROCESS_ENV.FINWAY_ADMIN_EMAIL) {
                return true
            }

            return employee.email !== PROCESS_ENV.FINWAY_ADMIN_EMAIL
        })

    return {
        employees,
        getProposedApprovers,
        selectApproverByCostCenter,
        eligibleRequesters,
    }
}
